import { subDays } from 'date-fns';
import { Payment } from 'payments-lib-types';
import { useEffect } from 'react';
import { useSWRConfig } from 'swr';
import { useShallow } from 'zustand/react/shallow';

import {
  ETransactionState,
  playerPaymentsWithPages as fetchPlayerPaymentsWithPages,
} from '@/api/profile';
import { toApiDate } from '@/helpers/dateTime';
import { groupMemberNoDeposits } from '@/helpers/user';
import { useGetCommonStore } from '@/store';

export const useInitStore = () => {
  const { mutate } = useSWRConfig();
  const useCommonStore = useGetCommonStore();

  const {
    isAuthenticated,
    pendingUserPure,
    user,
    currentWallet,
    lastSuccessfulPaymentsByCurrency,
  } = useCommonStore(
    useShallow((state) => ({
      isAuthenticated: state.isAuthenticated,
      pendingUserPure: state.pendingUserPure,
      user: state.user,
      currentWallet: state.currentWallet,
      lastSuccessfulPaymentsByCurrency: state.lastSuccessfulPaymentsByCurrency,
    }))
  );

  useEffect(() => {
    try {
      if (pendingUserPure) return;
      mutate('/info/project');
      mutate('/notification_center/centrifugo/subscription');
    } catch (error: unknown) {
      console.log(error);
    }
  }, [pendingUserPure, isAuthenticated, mutate]);

  useEffect(() => {
    if (
      !user ||
      groupMemberNoDeposits(user) ||
      currentWallet?.isCrypto ||
      lastSuccessfulPaymentsByCurrency[user.currency] !== undefined
    )
      return;
    useCommonStore.setState({ lastSuccessfulPaymentsByCurrencyPending: true });

    const to = toApiDate(new Date());
    const from = toApiDate(subDays(new Date(), 10));

    const controller = new AbortController();
    const signal = controller.signal;

    const timeoutId = setTimeout(() => controller.abort(), 5000);

    fetchPlayerPaymentsWithPages(
      {
        page: 1,
        filter: {
          currency: user.currency,
          type: Payment.Action.Deposit,
          from,
          to,
        },
      },
      signal
    )
      .then((page) => {
        if (!page) return;
        const transaction = page.data.find(
          ({ state, currency }) =>
            state === ETransactionState.Succeeded && currency === user.currency
        );
        useCommonStore.setState({
          lastSuccessfulPaymentsByCurrency: {
            ...lastSuccessfulPaymentsByCurrency,
            [user.currency]: transaction?.amountCents || 0,
          },
        });
      })
      .finally(() => {
        useCommonStore.setState({
          lastSuccessfulPaymentsByCurrencyPending: false,
        });
      });

    return () => {
      clearTimeout(timeoutId);
      controller.abort();
    };
  }, [user, currentWallet, lastSuccessfulPaymentsByCurrency]);

  return null;
};
